import * as React from "react";
import { ChevronDown } from "lucide-react";
import { cn } from "@/lib/format";

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  error?: boolean;
}

export const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, type, error, ...props }, ref) => {
    return (
      <input
        type={type}
        className={cn(
          "flex min-h-[42px] w-full rounded-md border  bg-white px-4 py-2  text-sm placeholder:text-muted-soft ring-primary disabled:cursor-not-allowed disabled:bg-bg disabled:opacity-60 transition-colors",
          error
            ? "border-danger focus-visible:ring-danger/40"
            : "border-headings/40 hover:border-muted-soft",
          className,
        )}
        ref={ref}
        aria-invalid={error || undefined}
        {...props}
      />
    );
  },
);
Input.displayName = "Input";

export interface TextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  error?: boolean;
}

export const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ className, error, ...props }, ref) => {
    return (
      <textarea
        className={cn(
          "flex min-h-28 w-full resize-y rounded-[var(--radius-inset)] border bg-white px-4 py-3 text-base sm:text-sm text-text placeholder:text-muted-soft ring-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/35 disabled:cursor-not-allowed disabled:bg-bg disabled:opacity-60 transition-colors",
          error
            ? "border-danger focus-visible:ring-danger/40"
            : "border-headings/30 hover:border-muted-soft",
          className,
        )}
        ref={ref}
        aria-invalid={error || undefined}
        {...props}
      />
    );
  },
);
Textarea.displayName = "Textarea";

export type SelectOptionValue = string | number;

export type SelectOptionItem = {
  value: SelectOptionValue;
  label: string;
  disabled?: boolean;
};

export type SelectOptionGroup = {
  label: string;
  disabled?: boolean;
  options?: readonly SelectOptionItem[];
  children?: readonly SelectOptionItem[];
};

export type SelectOption = SelectOptionItem | SelectOptionGroup;

export interface SelectProps extends React.SelectHTMLAttributes<HTMLSelectElement> {
  error?: boolean;
  options?: readonly SelectOption[];
  arrowClassName?: string;
}

function renderSelectOption(option: SelectOption) {
  if ("value" in option) {
    return (
      <option
        key={String(option.value)}
        value={option.value}
        disabled={option.disabled}
      >
        {option.label}
      </option>
    );
  }

  const nestedOptions = option.options ?? option.children ?? [];

  return (
    <optgroup
      key={option.label}
      label={option.label}
      disabled={option.disabled}
    >
      {nestedOptions.map((child) => (
        <option
          key={`${option.label}-${String(child.value)}`}
          value={child.value}
          disabled={child.disabled}
        >
          {child.label}
        </option>
      ))}
    </optgroup>
  );
}

export const Select = React.forwardRef<HTMLSelectElement, SelectProps>(
  (
    { className, error, options, children, arrowClassName, disabled, ...props },
    ref,
  ) => {
    return (
      <div className="relative w-full">
        <select
          className={cn(
            "peer flex min-h-11 w-full rounded-[var(--radius-inset)] border bg-white px-4 py-2.5 pr-11 text-base sm:text-sm text-text ring-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/35 disabled:cursor-not-allowed disabled:bg-bg disabled:opacity-60 transition-colors appearance-none cursor-pointer",
            error
              ? "border-danger focus-visible:ring-danger/40"
              : "border-line hover:border-muted-soft",
            className,
          )}
          ref={ref}
          disabled={disabled}
          aria-invalid={error || undefined}
          {...props}
        >
          {options ? options.map(renderSelectOption) : children}
        </select>
        <ChevronDown
          aria-hidden
          size={16}
          strokeWidth={2.25}
          className={cn(
            "pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-muted-soft transition-colors peer-focus-visible:text-primary peer-disabled:opacity-40",
            disabled ? "opacity-40" : null,
            arrowClassName,
          )}
        />
      </div>
    );
  },
);
Select.displayName = "Select";

export type LabelProps = React.LabelHTMLAttributes<HTMLLabelElement>;

export const Label = React.forwardRef<HTMLLabelElement, LabelProps>(
  ({ className, ...props }, ref) => (
    <label
      ref={ref}
      className={cn(
        "block text-xs font-medium mb-1.5 leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70",
        className,
      )}
      {...props}
    />
  ),
);
Label.displayName = "Label";

export interface DatePickerProps extends Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  "type"
> {
  error?: boolean;
}

export const DatePicker = React.forwardRef<HTMLInputElement, DatePickerProps>(
  ({ className, error, ...props }, ref) => {
    return (
      <input
        type="date"
        className={cn(
          "flex min-h-11 w-full rounded-[var(--radius-inset)] border bg-white px-4 py-2.5 text-base sm:text-sm text-text ring-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/35 disabled:cursor-not-allowed disabled:bg-bg disabled:opacity-60 transition-colors",
          // Tailwind pseudo-element styling for native date pickers
          "[&::-webkit-calendar-picker-indicator]:cursor-pointer [&::-webkit-calendar-picker-indicator]:opacity-60 hover:[&::-webkit-calendar-picker-indicator]:opacity-100",
          error
            ? "border-danger focus-visible:ring-danger/40"
            : "border-line hover:border-muted-soft",
          className,
        )}
        ref={ref}
        aria-invalid={error || undefined}
        {...props}
      />
    );
  },
);
DatePicker.displayName = "DatePicker";

export function Field({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return <div className={cn("space-y-1.5", className)}>{children}</div>;
}
